import {
  DESK_OPERATOR_HEADER,
  DESK_OPERATOR_REQUIRED_CODE,
  type DeskIdentifyResult,
  type DeskOperatorCurrent,
  type DeskOperatorEntry,
  type DeskOperatorSessionHolder,
  type DeskOperatorStore,
} from '../../deskOperator.js';

/**
 * The desk's `fetch`, handed to supabase-js as `global.fetch` in each app's
 * `supabase.ts`: every database request carries the tab's desk session
 * (`X-Desk-Operator`), so the audited writes can name the person. No session,
 * no header — the request is exactly what it was before.
 *
 * And the other half of O3: when the database refuses a write for want of a
 * person (`DK001`), the mounted « Qui êtes-vous ? » is asked, and once someone
 * has identified the SAME request is sent again with their session. The refused
 * write changed nothing (its transaction rolled back), so sending it again is
 * the write the desk meant. One place for every screen and every adapter.
 */
export function deskOperatorFetch(holder: DeskOperatorSessionHolder, base: typeof fetch = fetch.bind(globalThis)): typeof fetch {
  const send = (input: RequestInfo | URL, init: RequestInit | undefined) => {
    const sessionId = holder.get();
    if (sessionId === null) return base(input, init);
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    headers.set(DESK_OPERATOR_HEADER, sessionId);
    return base(input, { ...init, headers });
  };
  return async (input, init) => {
    const res = await send(input, init);
    const handler = holder.requiredHandler();
    // Only a refusal, and only a request that can be sent twice (a Request's body is read once).
    if (res.status < 400 || res.status >= 500 || handler === null || input instanceof Request) return res;
    const body = (await res.clone().json().catch(() => null)) as { code?: unknown } | null;
    if (body?.code !== DESK_OPERATOR_REQUIRED_CODE) return res;
    return (await handler()) ? send(input, init) : res;
  };
}

/** The slice of supabase-js the store needs — an RPC call and the session's token. */
export interface DeskOperatorRpcClient {
  rpc(fn: string, args?: Record<string, unknown>): PromiseLike<{ data: unknown; error: { message: string } | null }>;
  auth: { getSession(): Promise<{ data: { session: { access_token: string } | null } }> };
}

interface ListRow {
  id: string;
  first_name: string;
  last_name: string;
  nickname: string | null;
  has_pin: boolean;
  locked_until: string | null;
}

const REASONS = new Set(['notListed', 'locked', 'noPin', 'badPin', 'refused']);

/**
 * `DeskOperatorStore` for the desk. The list, « who am I » and « Changer » are
 * RPCs under the desk's own session; identify goes to the Function
 * (`/api/desk/identify`), which checks the kiosk PIN under the service role —
 * the PIN never reaches PostgREST. A successful identify stores the session id
 * in the holder, so every following request carries it.
 */
export function createSupabaseDeskOperatorStore(
  client: DeskOperatorRpcClient,
  holder: DeskOperatorSessionHolder,
  /** The identify Function's URL (the site project's `/api/desk/identify`). */
  identifyUrl: string,
  fetchImpl: typeof fetch = fetch.bind(globalThis),
): DeskOperatorStore {
  return {
    async list(): Promise<readonly DeskOperatorEntry[]> {
      const r = await client.rpc('desk_operator_list');
      if (r.error) throw new Error(r.error.message);
      return ((r.data ?? []) as ListRow[]).map((x) => ({
        id: x.id,
        firstName: x.first_name,
        lastName: x.last_name,
        nickname: x.nickname,
        hasPin: x.has_pin,
        lockedUntil: x.locked_until,
      }));
    },
    async current(): Promise<DeskOperatorCurrent | null> {
      const r = await client.rpc('desk_operator_current');
      if (r.error) throw new Error(r.error.message);
      const d = r.data as DeskOperatorCurrent | null;
      // An expired or ended session resolves to nobody: the tab forgets it.
      if ((d === null || !d.viaSession) && holder.get() !== null) holder.set(null);
      return d;
    },
    async identify(employeeId: string, pin: string): Promise<DeskIdentifyResult> {
      const { data } = await client.auth.getSession();
      const token = data.session?.access_token;
      if (!token) return { ok: false, reason: 'refused' };
      let res: Response;
      try {
        res = await fetchImpl(identifyUrl, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ employeeId, pin }),
        });
      } catch {
        return { ok: false, reason: 'unavailable' };
      }
      const body = (await res.json().catch(() => null)) as
        | { ok?: boolean; sessionId?: string; reason?: string; lockedUntil?: string }
        | null;
      if (body?.ok === true && typeof body.sessionId === 'string') {
        holder.set(body.sessionId);
        return { ok: true, sessionId: body.sessionId };
      }
      const reason = body && typeof body.reason === 'string' && REASONS.has(body.reason) ? body.reason : 'unavailable';
      return {
        ok: false,
        reason: reason as Exclude<DeskIdentifyResult, { ok: true }>['reason'],
        ...(body?.lockedUntil ? { lockedUntil: body.lockedUntil } : {}),
      };
    },
    async end(): Promise<void> {
      // Ended in the database first (the header is still sent), then forgotten.
      await client.rpc('desk_operator_end');
      holder.set(null);
    },
    onIdentifyRequired(handler) {
      holder.setRequiredHandler(handler);
      return () => {
        if (holder.requiredHandler() === handler) holder.setRequiredHandler(null);
      };
    },
  };
}
